import {
  InputAdornment,
  TextField,
  TextFieldProps,
} from '@material-ui/core';
import { ApiInfoRtn } from 'app/core/api/api-core/api-core.types';
import AutoHeightAnimation from 'app/shared/frames/animation/AutoHeightAnimation';
import { Opt } from 'app/tools/general.types';
import React, { useState, useEffect } from 'react';
import {
  Controller,
  ControllerRenderProps,
  UseControllerOptions,
} from 'react-hook-form';
import styled from 'styled-components/macro';
import { Except } from 'type-fest';
import IconButton from '@material-ui/core/IconButton';
import Icon from '../icon/Icon';
import IconLib from '../icon/icon-lib';
import { MarkupIfString } from '../misc';
import {
  SharedInputProps,
  sharedInputStyle,
} from './utilities/shared-input-style';

const StyledTextField = styled(TextField)<SharedInputProps>`
  ${sharedInputStyle}
`;

const InputPrefixIcon = styled(Icon)`
  margin-bottom: 2px;
`;

export type TextInputProps = {
  icon?: IconLib;
  errorText?: string | ApiInfoRtn;
  greyPlaceholder?: boolean;
  controller?: UseControllerOptions;
  minHeight?: number;
  textInputRef?: TextFieldProps['ref'];
} & Except<TextFieldProps, 'classes' | 'variant' | 'id'>;

export default function TextInput({
  icon,
  errorText: errorTextObj,
  greyPlaceholder,
  helperText,
  minHeight,
  textInputRef,
  error,
  controller,
  type,
  placeholder,
  InputProps,
  label: labelFromProps,
  ...props
}: TextInputProps): JSX.Element {
  const [inputType, setInputType] = useState(type);
  const [showPassword, setShowPassword] = useState(false);
  const errorText =
    typeof errorTextObj === 'string'
      ? errorTextObj
      : errorTextObj?.detail;
  const unifiedHelperText = errorTextObj || helperText;

  const startAdornment =
    InputProps?.startAdornment ||
    (icon && (
      <InputAdornment position="start">
        <InputPrefixIcon size={20} color="primary" {...{ icon }} />
      </InputAdornment>
    )) ||
    null;

  useEffect(() => {
    if (type === 'password') {
      setInputType(showPassword ? 'text' : 'password');
    }
  }, [type, showPassword]);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };
  const endAdornment =
    InputProps?.endAdornment ||
    (type === 'password' && (
      <InputAdornment position="end">
        <IconButton
          aria-label="toggle password visibility"
          onClick={handleClickShowPassword}
          onMouseDown={handleMouseDownPassword}
        >
          {showPassword ? (
            <Icon icon={IconLib.EYE_VISIBLE} />
          ) : (
            <Icon icon={IconLib.EYE_HIDDEN} />
          )}
        </IconButton>
      </InputAdornment>
    )) ||
    null;

  const controllerWithDefaults: Opt<UseControllerOptions> =
    controller && {
      defaultValue: '',
      ...controller,
    };

  const label = icon
    ? ''
    : labelFromProps || (greyPlaceholder ? placeholder : undefined);

  const TextFieldComponent = ({
    ref,
    ...controllerRenderProps
  }: Partial<ControllerRenderProps> = {}) => (
    <StyledTextField
      $greyPlaceholder={greyPlaceholder}
      $minHeight={minHeight}
      variant="outlined"
      InputProps={{
        ...InputProps,
        startAdornment,
        endAdornment,
      }}
      FormHelperTextProps={{ component: 'div' }}
      $noHelperText={!unifiedHelperText}
      helperText={
        <AutoHeightAnimation updateOn={unifiedHelperText ?? null}>
          <MarkupIfString content={unifiedHelperText} />
        </AutoHeightAnimation>
      }
      error={error || !!errorText}
      type={inputType || 'text'}
      inputRef={ref}
      ref={textInputRef}
      {...{ placeholder, label }}
      {...controllerRenderProps}
      {...props}
    />
  );

  return controllerWithDefaults ? (
    <Controller
      render={TextFieldComponent}
      {...controllerWithDefaults}
    />
  ) : (
    TextFieldComponent()
  );
}
